import * as React from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AddEmployeesToAttendancePolicyDocument,
  AttendancePolicyEmployeesCollectionListDocument,
  EmployeesEmployeeSelectorInput,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  policyEmployeesIds: number[]
  attendancePolicyId: number
  onClose: () => void
}

const AddEmployeesModal = ({
  policyEmployeesIds,
  setShowModal,
  attendancePolicyId,
  onClose,
}: Props) => {
  const [employeeSelection, setEmployeeSelection] = React.useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const [error, setError] = React.useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const [addEmployeesToAttendancePolicy] = useMutation(AddEmployeesToAttendancePolicyDocument, {
    extractorFn: (data) => data.attendanceMutations.addEmployeesAttendanceEmployeesPolicy,
    refetchQueries: [AttendancePolicyEmployeesCollectionListDocument],
  })

  const handleEmployeeChange = React.useCallback(
    (selection: EmployeeSelectorV2MultiSelectState) => {
      setEmployeeSelection(selection)
      setError(undefined)
    },
    []
  )

  const handleSubmit = async () => {
    if (
      employeeSelection.type === 'manual' &&
      (!employeeSelection.ids || employeeSelection.ids.length === 0)
    ) {
      setError(i18n.t('common.validations.empty'))
      return
    }

    setIsSubmitting(true)
    const result = await addEmployeesToAttendancePolicy({
      variables: {
        policyId: attendancePolicyId,
        employeeIds: [],
        employeeSelection,
      },
    })
    setIsSubmitting(false)

    const handled = handleResult(result, (errors) => {
      const errorMessage = Object.values(errors).join(', ')
      if (errorMessage) {
        setError(errorMessage)
      }
    })

    if (handled.success && handled.data?.employeesPolicy?.employeeIds) {
      onClose()
      await attendancePolicies.fetch()

      app.setFlash({
        type: 'correct',
        message: i18n.t('time_tracking.settings.employees.add.flash_messages.successful_update'),
        time: 2000,
      })
    }
  }

  return (
    <F0Dialog
      isOpen
      title={i18n.t('time_tracking.settings.employees.add.title')}
      description={i18n.t('time_tracking.settings.employees.add.description')}
      onClose={() => setShowModal(false)}
      primaryAction={{
        label: i18n.t('time_tracking.settings.employees.add.cta'),
        onClick: handleSubmit,
        disabled: isSubmitting,
        loading: isSubmitting,
      }}
      width="sm"
    >
      <EmployeeSelectorV2
        multiple
        onChange={handleEmployeeChange}
        forcedFilters={{
          excludeIds: policyEmployeesIds,
        }}
        error={error}
        options={{
          hideLabel: true,
          asList: true,
        }}
      />
    </F0Dialog>
  )
}

export default observer(AddEmployeesModal)
